import type { BasicQuestion } from "@repo/app-types/client/client";
import { Color } from "@repo/app-types/constants/colors";
import type {
  ComplexQuestion,
  ComplexQuestionInsert,
} from "@repo/app-types/entities/question";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea, TextareaVariant } from "@repo/ui/Inputs/textarea";
import { Pill } from "@repo/ui/Pills/pill";
import { IconTrash } from "@tabler/icons-react";
import type { FC } from "react";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";

interface QuestionsManagerCellProps {
  questionNumber: number;
  question: ComplexQuestion | ComplexQuestionInsert | BasicQuestion;
  autoFocus?: boolean;
  onRemove: () => void;
  onChange?: (question: BasicQuestion) => void;
  onEnterKeyDown?: () => void;
  /**
   * Invoked when the user pastes multiple questions separated by new lines.
   * The pasted questions will be provided as an array of strings.
   */
  onPasteQuestions?: (questions: string[]) => void;
}

export const QuestionsManagerCell: FC<QuestionsManagerCellProps> = ({
  questionNumber,
  question,
  autoFocus,
  onRemove,
  onChange,
  onEnterKeyDown,
  onPasteQuestions,
}) => {
  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <div className="group relative flex w-full cursor-pointer flex-row space-x-2 border-b border-gray-900/5 bg-white p-2">
          <div className="cursor-move">
            <Pill label={questionNumber} color={Color.Gray} size={Size.SMALL} />
          </div>
          <div className="w-full">
            <div className="w-full">
              <Textarea
                autoFocus={autoFocus}
                value={question.question}
                variant={TextareaVariant.MINIMAL}
                onChange={(newValue) => {
                  onChange?.({
                    ...question,
                    question: newValue,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && onEnterKeyDown) {
                    e.preventDefault();
                    e.currentTarget.blur();
                    onEnterKeyDown();
                  }
                }}
                onPaste={(e) => {
                  // Allow pasting multiple questions separated by new lines
                  const text = e.clipboardData.getData("text");
                  if (onPasteQuestions && text.includes("\n")) {
                    e.preventDefault();
                    const lines = text
                      .split(/\r?\n/)
                      .map((l) => l.trim())
                      .filter((l) => l.length > 0);
                    if (lines.length > 0) {
                      onPasteQuestions(lines);
                    }
                    return;
                  }
                }}
                placeholder="Add a question..."
                shouldDisableNewline
                isDisabled={!allowed}
              />
            </div>
          </div>
          {allowed ? (
            <div className="absolute top-1/2 right-3 z-10 ml-3 -translate-y-1/2 transform opacity-0 group-hover:opacity-100">
              <IconButton
                variant={ButtonVariant.Secondary}
                icon={<IconTrash className="h-4 w-4 text-red-500" />}
                onClick={onRemove}
                excludeFromTabOrder
              />
            </div>
          ) : null}
        </div>
      )}
    </Can>
  );
};
